import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import {
  getMoviesByCategory,
  getCategoryBySlug,
  movieCategories,
  siteInfo,
  type Movie,
} from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const allCategoryMovies = getMoviesByCategory(slug || '');
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');
  const [currentTab, setCurrentTab] = useState(slug || '');

  const sortedMovies = [...allCategoryMovies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'year') return b.year - a.year;
    return 0;
  });

  const filteredMovies = sortedMovies;
  const categoryName = category?.name || slug || '全部';
  const categoryDesc = category?.description || '探索所有精彩电影';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${categoryName} - ${siteInfo.name}`,
        description: `${categoryDesc} - ${siteInfo.name}为您提供${categoryName}高清在线观看`,
        url: `https://www.97cinema.com/category/${slug}`,
        isPartOf: { '@type': 'WebSite', '@id': `${siteInfo.url}/#website` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.97cinema.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${categoryName} - 高清免费在线观看 | 97影院`}
        description={`97影院${categoryName}频道，提供${categoryDesc}。高清蓝光画质，流畅不卡顿，免费在线观看。`}
        keywords={`${categoryName},${categoryName}电影,${categoryName}在线观看,97影院,免费电影`}
        canonical={`https://www.97cinema.com/category/${slug}`}
      />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName },
                ]}
              />

              <div className="mt-6">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                  {categoryName}
                </h1>
                <p className="mt-2 text-sm text-foreground-400">{categoryDesc}</p>
              </div>

              <div className="mt-6 flex items-center gap-2 flex-wrap">
                {movieCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setCurrentTab(cat.slug)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      currentTab === cat.slug
                        ? 'bg-primary-500 text-foreground-50'
                        : 'bg-background-100 text-foreground-300 hover:text-foreground-50 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3">
                <span className="text-sm text-foreground-400">排序：</span>
                {[
                  { value: 'default' as const, label: '默认' },
                  { value: 'rating' as const, label: '评分最高' },
                  { value: 'year' as const, label: '最新发布' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-primary-500/20 text-primary-500'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
                <span className="ml-auto text-sm text-foreground-500">共 {filteredMovies.length} 部</span>
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              {filteredMovies.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-movie-line text-2xl"></i>
                  </div>
                  <p className="mt-4 text-foreground-400">暂无该分类的电影</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTopButton />
      </div>
    </>
  );
}

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/movie/${movie.id}`}
      className="group block bg-background-100 rounded-lg overflow-hidden border border-background-200 hover:border-primary-500/50 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={movie.posterUrl}
          alt={movie.title}
          title={`${movie.title} - 97影院${movie.category}高清在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {movie.isNew && (
            <span className="px-2 py-0.5 bg-accent-500 text-background-950 text-xs font-semibold rounded whitespace-nowrap">新</span>
          )}
          {movie.isHot && (
            <span className="px-2 py-0.5 bg-primary-500 text-foreground-50 text-xs font-semibold rounded whitespace-nowrap">热播</span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 bg-background-950/70 text-accent-500 text-xs font-heading font-bold rounded whitespace-nowrap">
            {movie.rating}
          </span>
        </div>
        <div className="absolute bottom-2 left-2 right-2">
          <h3 className="text-sm font-semibold text-foreground-50 line-clamp-1">{movie.title}</h3>
          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-300">
            <span>{movie.year}</span>
            <span className="w-0.5 h-0.5 rounded-full bg-foreground-500"></span>
            <span>{movie.category}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}